import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileKindParams, type ProfileRouteParams } from '../profileRoute';
import { profilePath } from '../useProfileRoute';
import { useProfileMake } from '../useProfileMake';
import { useProfileUpgrade } from '../useProfileUpgrade';
import { useCopyText } from '../useCopyText';
import { useProfileZip } from '../useProfileZip';
import { profileDownloadUrl, retryItemOf } from '../profileAssets';
import { useMarkProfileAssetMutation } from '../../../../../redux/api/fymAPI';
import type { ProfileCardAction } from './profileCardModel';

/**
 * The clicks of the Studio "Your profile" card (design profile-card a, note "Route"): the card and
 * "Open profile" open Studio → Profile, a thumbnail opens its tool, Spotify opens the upload sheet,
 * "Make" makes the image from the blurred cover of the newest release (free), "I set it" marks a
 * banner, "Copy short" copies the bio, "Download all" makes the zip.
 */
export function useProfileCardActions(accountId: number) {
  const navigate = useNavigate();
  const make = useProfileMake(accountId);
  const zip = useProfileZip(accountId);
  const { copy, copied } = useCopyText();
  const { upgrade } = useProfileUpgrade();
  const [mark, markState] = useMarkProfileAssetMutation();

  const open = useCallback(
    (params: ProfileRouteParams = {}) => {
      // eslint-disable-next-line no-void
      void navigate(profilePath(params));
    },
    [navigate]
  );

  const run = useCallback(
    (action: ProfileCardAction) => {
      switch (action.kind) {
        case 'sendSpotify':
        case 'connectSpotify':
        case 'reconnectSpotify':
          open({ sheet: 'spotify' });
          return;
        case 'upgradeSpotify':
          upgrade();
          return;
        case 'iSetIt':
          mark({ accountId, assetUuid: action.assetUuid, set: true }).catch(console.error);
          return;
        case 'make':
          make
            .make([{ kind: action.profileKind, source: 'cover', catalogItemUuid: action.catalogItemUuid }])
            .catch(console.error);
          return;
        case 'retry': {
          const item = retryItemOf(action.asset);
          if (item === null) {
            open(profileKindParams(action.asset.kind));
            return;
          }
          make.make([item]).catch(console.error);
          return;
        }
        case 'openKind':
          open(profileKindParams(action.profileKind));
          return;
        case 'addPhoto':
          open({ tool: 'pressPhoto' });
          return;
        case 'download': {
          const url = profileDownloadUrl(action.asset);
          if (url !== null) {
            window.open(url, '_blank', 'noopener');
          }
          return;
        }
        case 'copyShort':
          copy('bioShort', action.text);
          return;
        case 'writeBio':
          open({ tool: 'bio' });
          return;
        default: {
          const exhaustive: never = action;
          console.error('Unknown profile card action', exhaustive);
        }
      }
    },
    [accountId, copy, make, mark, open, upgrade]
  );

  return {
    run,
    open,
    make,
    zip,
    copied,
    busy: make.isBusy || markState.isLoading,
  };
}
